import { useEffect, useState } from "react";
import axios from "axios";
import API_URL from "../../config";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PriceQueryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    status: "New",
    priority: "Medium",
    followUpDate: "",
    remark: "",
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/price-query/price-query/${id}`,
      );

      setData(res.data.data);

      setForm({
        status: res.data.data.status || "New",
        priority: res.data.data.priority || "Medium",
        followUpDate: "",
        remark: "",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/price-query/price-query/${id}`, {
        status: form.status,
        priority: form.priority,
      });

      if (form.remark) {
        await axios.post(
          `${API_URL}/api/price-query/price-query/${id}/followup`,
          {
            note: form.remark,
            createdBy: "admin",
            nextFollowUpDate: form.followUpDate,
          },
        );
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  if (!data) return <div className="text-black p-4">Loading...</div>;

  return (
    <div className="py-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-black">{data.organization}</h4>
          <small className="text-secondary">Price Query Details</small>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn"
            style={{ background: "#fff", color: "#000" }}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} /> Back
          </button>

          <button
            className="btn"
            style={{ background: "#22c55e", color: "#fff" }}
            onClick={() => setShowModal(true)}
          >
            + Update
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* LEFT */}
        <div className="col-md-8">
          {/* INFO CARD */}
          <div
            className="p-3 rounded-4 mb-3 card"
            // style={{ background: "#0f172a" }}
          >
            <h6 className="mb-3 text-black">Organization Info</h6>

            <p>
              <strong>Email:</strong> {data.email}
            </p>
            <p>
              <strong>Phone:</strong> {data.phone}
            </p>
            <p>
              <strong>Country:</strong> {data.country}
            </p>
            <p>
              <strong>State:</strong> {data.state}
            </p>
            <p>
              <strong>Website:</strong> {data.website}
            </p>
            <p>
              <strong>Contact Person:</strong> {data.contactPerson}
            </p>

            <p>
              <strong>Comments:</strong>
            </p>
            <div className="p-2 rounded text-black">
              {data.comments || "N/A"}
            </div>
          </div>

          {/* TIMELINE */}
          <div className="p-3 rounded-4 card" style={{ background: "#0f172a" }}>
            <h6 className="mb-3 text-black">Follow-ups</h6>

            {data.followUps?.length > 0 ? (
              [...data.followUps]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((f, i) => (
                  <div key={i} className="mb-3 p-2 rounded bg-dark">
                    <small className="text-secondary">
                      {new Date(f.date).toLocaleString()}
                    </small>

                    <p className="mb-1">{f.note}</p>

                    <small className="text-secondary">By: {f.createdBy}</small>

                    {f.nextFollowUpDate && (
                      <div>
                        <small className="text-info">
                          Next:{" "}
                          {new Date(f.nextFollowUpDate).toLocaleDateString()}
                        </small>
                      </div>
                    )}
                  </div>
                ))
            ) : (
              <p className="text-secondary">No updates yet</p>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-md-4">
          <div className="p-3 rounded-4 card">
            <h6 className="mb-3 text-black">Quick Info</h6>

            <p>
              Status:
              <span className="badge bg-primary ms-2">{data.status}</span>
            </p>

            <p>Priority: {data.priority}</p>
            <p>Follow-ups: {data.followUps?.length || 0}</p>

            <p>
              Created:
              <br />
              <small>{new Date(data.createdAt).toLocaleString()}</small>
            </p>

            <p>
              Updated:
              <br />
              <small>{new Date(data.updatedAt).toLocaleString()}</small>
            </p>
          </div>
        </div>
      </div>

      {/* 🔥 MODAL */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(6px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "600px",
              background: "#fff",
              color: "#000",
              borderRadius: "12px",
              padding: "24px",
            }}
          >
            <h5 className="mb-3">Update Price Query</h5>

            <div className="row g-3">
              <div className="col-md-6">
                <label>Status</label>
                <select
                  className="form-control"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="New">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="lost">Lost</option>
                </select>
              </div>

              <div className="col-md-6">
                <label>Priority</label>
                <select
                  className="form-control"
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>

              <div className="col-md-6">
                <label>Next Follow-up Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.followUpDate}
                  onChange={(e) =>
                    setForm({ ...form, followUpDate: e.target.value })
                  }
                />
              </div>

              <div className="col-12">
                <label>Remark</label>
                <textarea
                  className="form-control"
                  value={form.remark}
                  onChange={(e) => setForm({ ...form, remark: e.target.value })}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4 gap-2">
              <button
                className="btn"
                style={{ background: "#334155", color: "#fff" }}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn"
                style={{ background: "#22c55e", color: "#fff" }}
                onClick={handleSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceQueryDetails;
